import { useCallback, useEffect, useRef, useState } from 'react'

import { SsoStatus, SsoType } from '@tribeplatform/gql-client/types'
import {
  useAuthToken,
  useLoginWithSso,
  useSsos,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'
import { useQuery } from '@tribeplatform/slate-kit/utils'

import { getSsoCallbackUrl } from '../../utils/sso'
import { SsoIcon } from './SsoIcons'

export const Ssos = (props: {
  hasDivider: boolean
  invitationLinkId?: string
  invitationToken?: string
}) => {
  const redirectedAutomatically = useRef(false)
  const [loadingType, setLoadingType] = useState<SsoType>()
  const { hasDivider, invitationLinkId, invitationToken } = props
  const {
    data: { network },
  } = useAuthToken()
  const { data: enabledSsos } = useSsos({
    variables: { status: SsoStatus.ENABLE },
  })
  const { login } = useLoginWithSso()
  const query = useQuery()

  const onClick = useCallback(
    async type => {
      setLoadingType(type)
      const callbackUrl = getSsoCallbackUrl(
        type,
        network.domain,
        query.redirect,
      )

      const response = await login({
        input: {
          type,
          callbackUrl,
          invitationLinkId,
          invitationToken,
        },
      }).catch(e => {
        setLoadingType(undefined)
        throw e
      })

      window.location.href = response?.url
    },
    [login, network.domain, invitationLinkId, invitationToken, query.redirect],
  )
  const enabledVisibleSsos = enabledSsos?.filter(
    sso => sso.type !== SsoType.JWT,
  )

  useEffect(() => {
    const oauth2 = enabledVisibleSsos?.find(sso => sso.type === SsoType.OAUTH2)
    if (
      !redirectedAutomatically.current &&
      network.hideDefaultAuthenticationForm &&
      enabledVisibleSsos?.length === 1 &&
      oauth2
    ) {
      // failsafe
      redirectedAutomatically.current = true
      onClick(SsoType.OAUTH2)
    }
  }, [enabledVisibleSsos, onClick, network.hideDefaultAuthenticationForm])

  if (!enabledVisibleSsos?.length) return null

  return (
    <>
      {hasDivider ? <Divider>Or continue with</Divider> : null}
      <div className="grid grid-cols-1 gap-3">
        {enabledVisibleSsos.map(({ name, buttonText, type }) => (
          <Button
            href="#"
            onClick={() => onClick(type)}
            className="justify-center"
            key={type}
            variant="outline"
            size="lg"
            loading={type === loadingType}
            leadingIcon={
              SsoIcon[type] && <span className="mr-2">{SsoIcon[type]}</span>
            }
          >
            {buttonText || name}
          </Button>
        ))}
      </div>
    </>
  )
}
